import styled from '@emotion/styled';
import { Pagination as AntdPagination, Empty } from 'antd';
import type { HTMLAttributes, KeyboardEvent } from 'react';
import tw from 'twin.macro';

import type { HeaderConfig, Pagination } from './Header';
import Header from './Header';

interface Props<Item> {
  onClickItem?: (data: Item) => void;
  itemKey: (data: Item) => string;
  dataSource?: Item[];
  renderItem: (data: Item) => JSX.Element;
  pagination?: Pagination;
  headerConfig?: HeaderConfig;
  emptyText?: string;
}

const Grid = <Item extends Record<string, unknown>>({
  onClickItem,
  itemKey,
  dataSource = [],
  renderItem,
  pagination,
  headerConfig,
  emptyText = '',
}: Props<Item>) => {
  const isEmpty = dataSource.length === 0;
  return (
    <div css={tw`flex flex-col`}>
      {headerConfig ? (
        <Header {...headerConfig} pagination={pagination} />
      ) : undefined}

      {isEmpty ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={emptyText || '검색 결과가 없습니다.'}
        />
      ) : (
        <GridWrapper>
          {dataSource.map((item) => {
            const a11yProps: HTMLAttributes<HTMLDivElement> = onClickItem
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => {
                    onClickItem?.(item);
                  },
                  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter') {
                      onClickItem?.(item);
                    }
                  },
                }
              : {};

            return (
              <div
                {...a11yProps}
                css={[
                  tw`[& > *]:(w-full)`,
                  !!onClickItem && tw`cursor-pointer`,
                ]}
                key={itemKey(item)}
              >
                {renderItem(item)}
              </div>
            );
          })}
        </GridWrapper>
      )}
      {pagination && (
        <AntdPagination
          align="center"
          total={pagination.total ?? 0}
          current={pagination.query.page ?? 1}
          pageSize={pagination.query.size}
          onChange={(page, size) =>
            pagination.setPaginationValues({
              page,
              size,
            })
          }
          showQuickJumper={false}
          showSizeChanger={false}
        />
      )}
    </div>
  );
};

export default Grid;

const GridWrapper = styled.div`
  ${tw`grid grid-cols-3 mb-[32px] x768:grid-cols-2 x480:grid-cols-1 gap-[16px]`}
`;
